import _ from 'lodash';
import { useEffect, useState } from 'react';
import Ajv from 'ajv';
import { useDebouncedCallback } from 'use-debounce';
import { uiSchemaProperties } from '../../../shared/enums/uiSchemaProperties';
import autoFillApi from '../../../api/autoFill.api';
import { searchFieldById } from '../../../shared/utils/objectUtils';
import { getFormKeyBySchemaId } from '../rjsf/utils';
import { removeLeadingWhitespaceForValue } from '../../../shared/utils/stringUtils';
import { clearFields, getParentId, getParentTarget, isConditionMet } from '../../../shared/validations/inputParser';
import useAlertDialog from '../../../redux/features/alertDialog/useAlertDialog';

const useAutoFilingIntegration = (props) => {
  const { formContext = {}, onChange, uiSchema = {}, schema = {}, id, registry } = props;

  const [isAlreadyAutoFilled, setIsAlreadyAutoFilled] = useState(false);
  const [hasSchemaErrors, setHasSchemaErrors] = useState(false);
  const delay = ms => new Promise(res => setTimeout(res, ms));

  const autoFilingTrigger = uiSchema[uiSchemaProperties.UI_WIDGET_AUTO_FILL_TRIGGER];
  const rootLevel = autoFilingTrigger?.rootLevel || 1;
  const widgetTriggerSkip = autoFilingTrigger?.skip || [];
  const [value, setValue] = useState(props?.value);
  const targetFields = autoFilingTrigger?.target || [];
  const integrationPath = autoFilingTrigger?.integrationPath;
  const trigger = autoFilingTrigger?.trigger;
  const targetId = getParentTarget(id, rootLevel);
  const currentSchemaKey = getFormKeyBySchemaId(id, rootLevel);
  const ajv = new Ajv();
  const configuredParams = autoFilingTrigger?.requestParams || [];
  const { openAlertDialog } = useAlertDialog();

  const isValidated = () => {
    const isValid = searchFieldById(formContext, `${id}Validated`);
    setIsAlreadyAutoFilled(isValid);
    return isValid;
  };

  const setValidState = async (_isValid) => {
    const target = `${id}Validated`?.replace('submission_', '').replaceAll('_', '.');
    await registry.setFormData(_isValid, target);
  };

  useEffect(() => {
    if (!isValidated()) {
      onBlurDebounced(null, value);
    }
  }, []);

  const onInputReplace = async (_value) => {
    await onValidationEnd(false);
    await clearFields(targetFields, registry, currentSchemaKey);
    setValue(_value);
    onChange(_value);
  };

  const addSquareBracketsToNumbers = (str) => {
    const regex = /\d+/g;
    const numbers = str.match(regex);
    if (!numbers || numbers.length === 0) {
      return str;
    }
    return str.replace(regex, (match) => {
      return `[${match}]`;
    });
  };

  const onValidationEnd = async (isValid) => {
    await setIsAlreadyAutoFilled(isValid);
    await setDisablingFields(isValid);
  };

  const setDisablingFields = async (isValid = false) => {
    let disablingFields = formContext?.submission?.disablingFields || [];
    const _tempArr = [];
    for (const targetField of targetFields) {
      const _target = `${targetId}.${targetField}`;
      _tempArr.push(_target);
    }
    disablingFields = isValid ? _.union(disablingFields, _tempArr) : _.difference(disablingFields, _tempArr);
    disablingFields = disablingFields.filter((_field) => {
      const target = addSquareBracketsToNumbers(_field);
      return !_.isEmpty(_.get(formContext?.submission, target));
    });
    await setValidState(isValid);
    await registry.setFormData(disablingFields, 'disablingFields');
  };

  const callAPI = async (parentValue, _value, params) => {
    const data = await autoFillApi.fill(integrationPath, _value, params);
    const isValid = !_.isEmpty(data) && !!rootLevel && !!_value;
    if (isValid) {
      let newData = _.merge(parentValue, data);
      if (widgetTriggerSkip) {
        newData = _.omit(newData, widgetTriggerSkip);
      }
      await onChange(_value);
      await delay(1000);
      await registry.setFormData(newData, targetId);
    }
    await onValidationEnd(isValid);
    return false;
  };

  const getRequestParams = (parentValue) => {
    const params = {};
    for (const configuredParam of configuredParams) {
      const paramValue = parentValue[configuredParam.targetValue];
      params[configuredParam.key] = paramValue;
    }
    return params;
  };

  const callIntegrationFn = async (_value) => {
    const parentValue = searchFieldById(formContext, getParentId(id, rootLevel));
    if (!isConditionMet(trigger, parentValue)) {
      await onValidationEnd(false);
      return false;
    }
    const params = getRequestParams(parentValue);
    return callAPI(parentValue, _value, params);
  };

  const onBlurDebounced = useDebouncedCallback(async (_id, _value) => {
    if (!hasSchemaErrors && !!integrationPath) {
      await callIntegrationFn(_value);
    }
  }, 500);

  const onInputChange = (v) => {
    if (isAlreadyAutoFilled) {
      openAlertDialog({
        updatedTexts: {
          title: 'autocomplete.modal.title',
          message: 'autocomplete.applicant.patent.modal.message',
          confirmButton: 'autocomplete.modal.confirm',
          cancelButton: 'actions.cancel',
        },
        updatedSettings: {
          showCancelButton: true,
          showCloseButton: true,
        },
        acceptHandler: {
          func: onInputReplace,
          args: v,
        },
      });
      return;
    }
    const _value = removeLeadingWhitespaceForValue(v);
    const validByJsonSchema = ajv.validate(schema, _value);
    setHasSchemaErrors(!validByJsonSchema);
    setValue(_value);
    onChange(_value);
  };

  return {
    value,
    hasSchemaErrors,
    onInputChange,
    onBlurDebounced,
    isAlreadyAutoFilled,
    onInputReplace,
  };
};

export default useAutoFilingIntegration;
